import { useEffect, useMemo, useState } from "react";
import {
  isCloudIx,
  isComponentRequired,
  validateComponent,
} from "../utils/componentListRules";
import { scrollToIslandTop } from "../../shared/forms/scroll";
import useRecaptcha from "../../shared/hooks/useRecaptcha";
import { getOrganisationList, submitTechnicalSupportRequest } from "../services/contactApi";
import { mapSubmitPayload } from "../utils/payloadMappers";
import {
  createTechnicalSupportSchema,
  mapZodIssuesToFieldErrors,
} from "../utils/validationSchema";

function normaliseServerErrors(body, unexpectedErrorMsg) {
  if (!body) {
    return ["Unexpected error."];
  }

  if (Array.isArray(body)) {
    return body;
  }

  if (typeof body === "string") {
    return [body];
  }

  if (body.message) {
    return [`${body.message} ${unexpectedErrorMsg || ""}`.trim()];
  }

  return ["Unexpected error."];
}

export default function useTechnicalSupportForm(config) {
  const [form, setForm] = useState(() => ({
    componentList: config.componentList || [],
    firstNameQA: "",
    surnameQA: "",
    emailQA: "",
    emailQAConfirm: "",
    phoneQA: "",
    organisationQA: "",
    organisationIdQA: "",
    otherOrganisationQA: "",
    tandcQA: false,
    filesQA: [],
  }));
  const [organisations, setOrganisations] = useState([]);
  const [showDropdownList, setShowDropdownList] = useState(false);
  const [componentErrors, setComponentErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverSideErrors, setServerSideErrors] = useState([]);
  const [submitResult, setSubmitResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const { getResponse: getRecaptchaResponse, reset: resetRecaptcha } = useRecaptcha({
    sitekey: config.captchaKey,
    onSuccess: () => setRecaptchaError(""),
  });

  useEffect(() => {
    getOrganisationList(config.isGasConsumerQuery)
      .then((items) => setOrganisations(Array.isArray(items) ? items : []))
      .catch(() => setOrganisations([]));
  }, [config.isGasConsumerQuery]);

  const filteredItems = useMemo(() => {
    const query = form.organisationQA.trim();
    const other = config.allowNotListedOrganisation
      ? [
          {
            id: -1,
            shortCode: "OTHER",
            name: "Organisation not listed?",
            combinedTitle: "Organisation not listed?",
            gasSupplier: false,
          },
        ]
      : [];

    if (!query) {
      return [];
    }

    const parts = query
      .split(/\s+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);

    const matches = organisations.filter((item) =>
      parts.every((part) => (item.combinedTitle || "").toLowerCase().includes(part)),
    );

    const exact = organisations.filter((item) => item.combinedTitle === query);
    return exact.length === 1 ? exact : other.concat(matches);
  }, [config.allowNotListedOrganisation, form.organisationQA, organisations]);

  const filesByFieldId = useMemo(() => {
    return form.filesQA.reduce((acc, file) => {
      const key = file.id;
      acc[key] = acc[key] || [];
      acc[key].push(file);
      return acc;
    }, {});
  }, [form.filesQA]);
  const validationSchema = useMemo(
    () => createTechnicalSupportSchema(config),
    [config],
  );

  function getFieldErrors(nextForm) {
    const organisationIsValidSelection =
      config.isGasConsumerQuery ||
      (nextForm.organisationIdQA !== "" &&
        filteredItems.length >= 1 &&
        Number(nextForm.organisationIdQA) === Number(filteredItems[0].id));
    const schemaResult = validationSchema.safeParse({
      firstNameQA: nextForm.firstNameQA,
      surnameQA: nextForm.surnameQA,
      emailQA: nextForm.emailQA,
      emailQAConfirm: nextForm.emailQAConfirm,
      phoneQA: nextForm.phoneQA,
      organisationQA: nextForm.organisationQA,
      organisationIdQA: nextForm.organisationIdQA,
      organisationHasValidSelection: organisationIsValidSelection,
      otherOrganisationQA: nextForm.otherOrganisationQA,
      tandcQA: nextForm.tandcQA,
    });

    return schemaResult.success
      ? {}
      : mapZodIssuesToFieldErrors(schemaResult.error.issues);
  }

  function getComponentErrors(nextForm) {
    const nextComponentErrors = {};

    nextForm.componentList.forEach((component) => {
      const error = validateComponent(component, nextForm.componentList);
      if (error) {
        nextComponentErrors[component.id] = error;
      }

      if (
        component.type === "file_upload" &&
        isComponentRequired(component, nextForm.componentList) &&
        !(nextForm.filesQA || []).some((file) => file.id === component.id)
      ) {
        nextComponentErrors[component.id] = "required";
      }
    });

    return nextComponentErrors;
  }

  function syncValidationState(nextForm, force = false) {
    if (!hasAttemptedSubmit && !force) {
      return;
    }
    setFieldErrors(getFieldErrors(nextForm));
    setComponentErrors(getComponentErrors(nextForm));
  }

  useEffect(() => {
    if (!hasAttemptedSubmit) {
      return;
    }

    setFieldErrors(getFieldErrors(form));
    setComponentErrors(getComponentErrors(form));
  }, [filteredItems, form, hasAttemptedSubmit]);

  function updateComponentList(nextComponentList) {
    setForm((current) => {
      const nextForm = {
        ...current,
        componentList: nextComponentList,
      };
      syncValidationState(nextForm);
      return nextForm;
    });
  }

  function updateField(name, value) {
    setServerSideErrors([]);
    setForm((current) => {
      const nextForm = {
        ...current,
        [name]: value,
      };
      syncValidationState(nextForm);
      return nextForm;
    });
  }

  function updateComponentValue(id, value) {
    const nextComponentList = form.componentList.map((item) =>
      item.id === id ? { ...item, value } : item,
    );
    updateComponentList(nextComponentList);
  }

  function selectOrganisation(item) {
    setForm((current) => {
      const nextForm = {
        ...current,
        organisationQA: item.combinedTitle,
        organisationIdQA: item.id,
        otherOrganisationQA: item.id === -1 ? current.otherOrganisationQA : "",
      };
      syncValidationState(nextForm);
      return nextForm;
    });
    setShowDropdownList(false);
  }

  function addUploadedFile(targetField, uploadedFile, multiple) {
    setServerSideErrors([]);
    setForm((current) => {
      let nextFilesQA = [...current.filesQA];

      if (!multiple) {
        nextFilesQA = nextFilesQA.filter((item) => item.id !== targetField.id);
      }

      nextFilesQA = nextFilesQA.concat(uploadedFile);

      const nextForm = {
        ...current,
        filesQA: nextFilesQA,
        componentList: current.componentList.map((item) =>
          item.id === targetField.id
            ? { ...item, value: uploadedFile.name || item.value }
            : item,
        ),
      };
      syncValidationState(nextForm);
      return nextForm;
    });
  }

  function removeFileByNameGuid(nameGuid) {
    setForm((current) => {
      const removedFile = current.filesQA.find((item) => item.nameGuid === nameGuid);
      const nextFilesQA = current.filesQA.filter((item) => item.nameGuid !== nameGuid);
      const nextForm = {
        ...current,
        filesQA: nextFilesQA,
        componentList: current.componentList.map((item) =>
          removedFile &&
          item.id === removedFile.id &&
          !nextFilesQA.some((existing) => existing.id === removedFile.id)
            ? { ...item, value: "" }
            : item,
        ),
      };
      syncValidationState(nextForm);
      return nextForm;
    });
  }

  function validateForm() {
    const nextFieldErrors = getFieldErrors(form);
    const nextComponentErrors = getComponentErrors(form);

    const captchaResponse = getRecaptchaResponse();
    if (!captchaResponse) {
      setRecaptchaError("Complete captcha challenge");
    } else {
      setRecaptchaError("");
    }

    setFieldErrors(nextFieldErrors);
    setComponentErrors(nextComponentErrors);

    return {
      valid:
        !Object.keys(nextFieldErrors).length &&
        !Object.keys(nextComponentErrors).length &&
        Boolean(captchaResponse),
      captchaResponse,
    };
  }

  async function submitForm(event) {
    event.preventDefault();
    setHasAttemptedSubmit(true);
    setServerSideErrors([]);

    const { valid, captchaResponse } = validateForm();
    if (!valid) {
      scrollToIslandTop("technical-support-request-island");
      return;
    }

    setLoading(true);

    try {
      const payload = mapSubmitPayload(config, form, captchaResponse);
      const response = await submitTechnicalSupportRequest(payload);
      setSubmitResult(response);
      if (typeof window.logGAEvent === "function") {
        window.logGAEvent("Help & Support", "Form Submit Success", document.location.href);
      }
      scrollToIslandTop("technical-support-request-island");
    } catch (error) {
      const errors = normaliseServerErrors(error.body, config.unexpectedErrorMsg);
      if (error.body?.ResponseCode || error.body?.responseCode) {
        setSubmitResult(error.body);
      } else {
        setServerSideErrors(errors);
        resetRecaptcha();
        setRecaptchaError("");
      }
      scrollToIslandTop("technical-support-request-island");
    } finally {
      setLoading(false);
    }
  }

  return {
    form,
    organisations,
    filteredItems,
    filesByFieldId,
    showDropdownList,
    setShowDropdownList,
    componentErrors,
    fieldErrors,
    serverSideErrors,
    submitResult,
    loading,
    uploading,
    recaptchaError,
    isCloudIx: isCloudIx(form.componentList),
    updateField,
    updateComponentValue,
    selectOrganisation,
    addUploadedFile,
    removeFileByNameGuid,
    submitForm,
  };
}
